import React, { useMemo } from 'react';
import {
	Box,
	Button,
	Divider,
	FormControl,
	IconButton,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
	BspMarket,
	findAdjacentBspPeriod,
	findBspPeriod,
	findBspPeriodForDate,
	getBspPeriods,
	parseBspCreatedValue,
} from 'features/common/constants/bspPeriods';
import {
	CHECK_IN_DATE_PRESETS,
	CIVIL_DATE_PRESETS,
	CIVIL_DATE_PRESET_LABELS,
	CheckInDatePreset,
	CivilDatePreset,
	DateRangePreset,
	formatDateRangeLabel,
	getDateRangeFromPreset,
} from 'features/common/helpers/datePresets';
import { buildBspCreatedValue, ServicesQuickDateState } from '../../helpers/servicesQuickDateFilters';

interface ServicesQuickDateBarProps {
	quickDateState: ServicesQuickDateState;
	onQuickDateChange: (next: ServicesQuickDateState) => void;
}

const PRESET_BUTTON_SX = { minWidth: 'auto', px: 1.25, py: 0.35, fontSize: '0.8125rem', textTransform: 'none' };

const ACTIVE_FILTER_BADGE_SX = {
	position: 'absolute',
	top: -7,
	right: -7,
	fontSize: 13,
	bgcolor: 'background.paper',
	borderRadius: '50%',
	border: '1px solid',
	borderColor: 'primary.main',
	color: 'primary.main',
	p: '1px',
	pointerEvents: 'none',
	boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
	zIndex: 1,
};

interface ToggleFilterButtonProps {
	isActive: boolean;
	tooltip: string;
	onClick: () => void;
	disabled?: boolean;
	children: React.ReactNode;
}

const ToggleFilterButton = ({
	isActive,
	tooltip,
	onClick,
	disabled,
	children,
}: ToggleFilterButtonProps): JSX.Element => (
	<Tooltip title={isActive ? `${tooltip} — Clic para quitar` : tooltip}>
		<Box component="span" sx={{ position: 'relative', display: 'inline-flex' }}>
			<Button
				size="small"
				variant={isActive ? 'contained' : 'outlined'}
				onClick={onClick}
				disabled={disabled}
				aria-pressed={isActive}
				sx={PRESET_BUTTON_SX}
			>
				{children}
			</Button>
			{isActive && <CloseIcon sx={ACTIVE_FILTER_BADGE_SX} />}
		</Box>
	</Tooltip>
);

const PresetRow = <T extends DateRangePreset>({
	label,
	presets,
	activePreset,
	onToggle,
}: {
	label: string;
	presets: readonly T[];
	activePreset: T | '';
	onToggle: (preset: T) => void;
}): JSX.Element => (
	<Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
		<Typography variant="caption" color="text.secondary" sx={{ minWidth: 72, fontWeight: 600 }}>
			{label}
		</Typography>
		{presets.map((preset) => {
			const range = getDateRangeFromPreset(preset);
			const isActive = activePreset === preset;
			return (
				<ToggleFilterButton
					key={preset}
					isActive={isActive}
					tooltip={formatDateRangeLabel(range.from, range.to)}
					onClick={() => onToggle(preset)}
				>
					{CIVIL_DATE_PRESET_LABELS[preset]}
				</ToggleFilterButton>
			);
		})}
	</Box>
);

const BspMarketRow = ({
	market,
	activeBspCreated,
	onBspChange,
}: {
	market: BspMarket;
	activeBspCreated: string;
	onBspChange: (value: string) => void;
}): JSX.Element => {
	const parsedActive = parseBspCreatedValue(activeBspCreated);
	const isThisMarketActive = parsedActive?.market === market;
	const selectedPeriodName = isThisMarketActive ? parsedActive?.periodName ?? '' : '';

	const periods = useMemo(() => getBspPeriods(market), [market]);
	const currentPeriod = useMemo(() => findBspPeriodForDate(market), [market]);
	const previousPeriod = useMemo(
		() => (currentPeriod ? findAdjacentBspPeriod(market, currentPeriod, 'previous') : undefined),
		[market, currentPeriod],
	);

	const handlePeriodSelect = (event: SelectChangeEvent<string>) => {
		const periodName = event.target.value;
		if (!periodName) {
			onBspChange('');
			return;
		}
		onBspChange(buildBspCreatedValue(market, periodName));
	};

	const handleQuickPeriod = (periodName: string | undefined) => {
		if (!periodName) return;
		if (isThisMarketActive && selectedPeriodName === periodName) {
			onBspChange('');
			return;
		}
		onBspChange(buildBspCreatedValue(market, periodName));
	};

	const selectedPeriod = selectedPeriodName ? findBspPeriod(market, selectedPeriodName) : undefined;
	const isSelectBspActive =
		isThisMarketActive &&
		!!selectedPeriodName &&
		selectedPeriodName !== currentPeriod?.name &&
		selectedPeriodName !== previousPeriod?.name;

	return (
		<Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
			<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, minWidth: 28 }}>
				{market}
			</Typography>
			<ToggleFilterButton
				isActive={isThisMarketActive && selectedPeriodName === currentPeriod?.name}
				tooltip={currentPeriod ? `${currentPeriod.from_date} → ${currentPeriod.to_date}` : ''}
				onClick={() => handleQuickPeriod(currentPeriod?.name)}
				disabled={!currentPeriod}
			>
				Actual
			</ToggleFilterButton>
			<ToggleFilterButton
				isActive={isThisMarketActive && selectedPeriodName === previousPeriod?.name}
				tooltip={previousPeriod ? `${previousPeriod.from_date} → ${previousPeriod.to_date}` : ''}
				onClick={() => handleQuickPeriod(previousPeriod?.name)}
				disabled={!previousPeriod}
			>
				Anterior
			</ToggleFilterButton>
			<Box sx={{ position: 'relative', display: 'inline-flex' }}>
				<FormControl size="small" sx={{ minWidth: 220 }}>
					<Select
						displayEmpty
						value={isThisMarketActive ? selectedPeriodName : ''}
						onChange={handlePeriodSelect}
						renderValue={(value) => {
							if (!value || !isThisMarketActive) {
								return (
									<Typography variant="body2" color="text.secondary">
										Semana...
									</Typography>
								);
							}
							const period = findBspPeriod(market, value);
							return period?.label ?? value;
						}}
					>
						<MenuItem value="">
							<em>Sin filtro BSP {market}</em>
						</MenuItem>
						{periods.map((period) => (
							<MenuItem key={period.name} value={period.name}>
								{period.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{isSelectBspActive && (
					<Tooltip title="Quitar filtro BSP">
						<IconButton
							size="small"
							onClick={() => onBspChange('')}
							sx={{
								position: 'absolute',
								top: -8,
								right: -8,
								width: 18,
								height: 18,
								bgcolor: 'background.paper',
								border: '1px solid',
								borderColor: 'primary.main',
								color: 'primary.main',
								boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
								zIndex: 1,
								'&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
							}}
						>
							<CloseIcon sx={{ fontSize: 12 }} />
						</IconButton>
					</Tooltip>
				)}
			</Box>
			{selectedPeriod && isThisMarketActive && (
				<Typography variant="caption" color="text.secondary">
					Pago {selectedPeriod.payment_date}
				</Typography>
			)}
		</Box>
	);
};

export const ServicesQuickDateBar = ({ quickDateState, onQuickDateChange }: ServicesQuickDateBarProps): JSX.Element => {
	const handleCreatedToggle = (preset: CivilDatePreset) => {
		onQuickDateChange({
			...quickDateState,
			createdPreset: quickDateState.createdPreset === preset ? '' : preset,
			bspCreated: '',
		});
	};

	const handleCheckInToggle = (preset: CheckInDatePreset) => {
		onQuickDateChange({
			...quickDateState,
			checkInPreset: quickDateState.checkInPreset === preset ? '' : preset,
		});
	};

	const handleBspChange = (value: string) => {
		onQuickDateChange({
			...quickDateState,
			bspCreated: value,
			createdPreset: value ? '' : quickDateState.createdPreset,
		});
	};

	return (
		<Paper
			elevation={0}
			sx={{
				p: 1.5,
				mb: 2,
				borderRadius: 2,
				border: '1px solid rgba(0,0,0,0.08)',
				background: 'linear-gradient(180deg, #fafbff 0%, #ffffff 100%)',
			}}
		>
			<Box display="flex" flexDirection="column" gap={1}>
				<PresetRow
					label="Creación"
					presets={CIVIL_DATE_PRESETS}
					activePreset={quickDateState.createdPreset}
					onToggle={handleCreatedToggle}
				/>
				<PresetRow
					label="Check-in"
					presets={CHECK_IN_DATE_PRESETS}
					activePreset={quickDateState.checkInPreset}
					onToggle={handleCheckInToggle}
				/>
				<Divider flexItem />
				<Box display="flex" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
					<Typography variant="caption" color="text.secondary" sx={{ minWidth: 72, fontWeight: 600, pt: 0.75 }}>
						BSP creación
					</Typography>
					<BspMarketRow market="UY" activeBspCreated={quickDateState.bspCreated} onBspChange={handleBspChange} />
					<BspMarketRow market="PY" activeBspCreated={quickDateState.bspCreated} onBspChange={handleBspChange} />
				</Box>
			</Box>
		</Paper>
	);
};
